import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import InputMask from "react-input-mask";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import { usePicklist } from "../../contexts/PicklistProvider";
import ClayLoadingIndicator from "@clayui/loading-indicator";

const BaseInfo = ({ data, updateData, onNextStep, onBackStep }) => {
	const { getPicklistByCode, externalReferenceCodes, loading } =
		usePicklist();

	const [cepLoading, setCepLoading] = useState(false);

	const fetchAddressByCep = async (cep, setFieldValue) => {
		const digits = cep.replace(/\D/g, "");
		if (digits.length !== 8) return;

		setCepLoading(true);
		try {
			const response = await fetch(
				`https://brasilapi.com.br/api/cep/v1/${digits}`
			);
			if (response.ok) {
				const address = await response.json();
				setFieldValue("endereco", address.street || "");
				setFieldValue("bairro", address.neighborhood || "");
				setFieldValue("cidade", address.city || "");
				setFieldValue("uf", address.state || "");
			}
		} catch (error) {
			console.error("Erro ao buscar CEP:", error);
		} finally {
			setCepLoading(false);
		}
	};

	const validateCPF = (cpf) => {
		if (!cpf) return false;
		cpf = cpf.replace(/[^\d]/g, "");

		if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
			return false;
		}

		let sum = 0;
		let remainder;

		for (let i = 1; i <= 9; i++) {
			sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
		}
		remainder = (sum * 10) % 11;
		if (remainder === 10 || remainder === 11) remainder = 0;
		if (remainder !== parseInt(cpf.substring(9, 10))) return false;

		sum = 0;
		for (let i = 1; i <= 10; i++) {
			sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
		}
		remainder = (sum * 10) % 11;
		if (remainder === 10 || remainder === 11) remainder = 0;
		if (remainder !== parseInt(cpf.substring(10, 11))) return false;

		return true;
	};

	const minDate = new Date();
	const day = String(minDate.getDate()).padStart(2, "0");
	const month = String(minDate.getMonth() + 1).padStart(2, "0");
	const minDateFormatted = `${minDate.getFullYear() - 18}-${month}-${day}`;

	const todayDateFormatted = `${minDate.getFullYear()}-${month}-${day}`;

	const ufPicklistItems = loading
		? []
		: getPicklistByCode(externalReferenceCodes.UF);

	const sexoPicklistItems = loading
		? []
		: getPicklistByCode(externalReferenceCodes.GENDER);

	const tipoSanguineoPicklistItems = loading
		? []
		: getPicklistByCode(externalReferenceCodes.BLOODTYPE);

	const nivelDeEscolaridadePicklistItems = loading
		? []
		: getPicklistByCode(externalReferenceCodes.EDUCATIONLEVEL);

	const estadoCivilPicklistItems = loading
		? []
		: getPicklistByCode(externalReferenceCodes.CIVILSTATUS);

	if (loading) {
		return <ClayLoadingIndicator />;
	}

	const validationSchema = Yup.object().shape({
		nome: Yup.string()
			.min(2, "Nome inválido")
			.required("Nome é obrigatório"),
		email: Yup.string()
			.email("Email inválido")
			.required("Email obrigatório"),
		cpf: Yup.string()
			.required("CPF é obrigatório")
			.test("valid-cpf", "CPF inválido", validateCPF),
		telefone: Yup.string().required("Telefone é obrigatório"),
		rg: Yup.number("RG inválido")
			.required("RG é obrigatório"),
		dataNascimento: Yup.date().required("Data de nascimento é obrigatória"),
		// sexo: Yup.string().required("Gênero é obrigatório"),
		nomePai: Yup.string()
			.min(2, "Nome do pai inválido")
			.required("Nome do pai é obrigatório"),
		nomeMae: Yup.string()
			.min(2, "Nome da mãe inválido")
			.required("Nome da mãe é obrigatório"),
		naturalidade: Yup.string().required("Naturalidade é obrigatória"),
		// tipoSanguineo: Yup.string().required("Tipo sanguíneo é obrigatório"),
		bairro: Yup.string().required("Bairro obrigatório"),
		cidade: Yup.string().required("Cidade obrigatório"),
		// uf: Yup.string().required("UF obrigatório"),
		cep: Yup.string()
			.min(9, "CEP inválido")
			.max(9, "CEP inválido")
			.required("CEP obrigatório"),
	});

	return (
		<div className="form-wrapper">
			<h4 className="mb-4">Informações Pessoais</h4>
			<Formik
				initialValues={{
					nome: data.nome || "",
					email: data.email || "",
					telefone: data.telefone || "",
					cpf: data.cpf || "",
					rg: data.rg || "",
					sexo: data.sexo || "",
					nomePai: data.nomePai || "",
					nomeMae: data.nomeMae || "",
					tipoSanguineo: data.tipoSanguineo || "",
					endereco: data.endereco || "",
					bairro: data.bairro || "",
					cidade: data.cidade || "",
					uf: data.uf || "",
					cep: data.cep || "",
					dataNascimento: data.dataNascimento || "",
					naturalidade: data.naturalidade || "",
					orgaoExpeditor: data.orgaoExpeditor || "",
					dataEmissao: data.dataEmissao || "",
					nivelDeEscolaridade: data.nivelDeEscolaridade || "",
					estadoCivil: data.estadoCivil || "",
				}}
				validationSchema={validationSchema}
				onSubmit={(values, actions) => {
					try {
						const sanitizedValues = {
							...values,
							cpf: values.cpf.replace(/[^\d]/g, ""),
							cep: values.cep.replace(/[^\d]/g, ""),
							telefone: values.telefone.replace(/[^\d]/g, ""),
						};
						updateData(sanitizedValues);
						onNextStep();
						actions.setSubmitting(false);
					} catch (error) {
						console.error("Error during submission:", error);
						actions.setSubmitting(false);
						actions.setStatus("Erro ao enviar o formulário");
					}
				}}
			>
				{({ errors, isSubmitting, submitCount, touched, setFieldValue }) => (
					<Form id="base-info-form">
						{submitCount > 0 && Object.keys(errors).length > 0 && (
							<div className="alert alert-danger mb-3">
								<span className="text-4">
									Verifique os campos abaixo para continuar:
								</span>
								<ul className="mb-0">
									{Object.entries(errors).map(
										([field, error]) => (
											<li key={field}>{error}</li>
										)
									)}
								</ul>
							</div>
						)}
						<div className="d-flex flex-column mb-3">
							<div className="row">
								<div className="form-group required d-flex flex-column col-md-4 col-12">
									<label
										htmlFor="nome"
										className="form-label"
									>
										Nome completo
									</label>
									<Field
										name="nome"
										type="text"
										className="form-control form-field"
										placeholder="José de Almeida Santos"
									/>
									{errors.nome && touched.nome ? (
										<div className="invalid-feedback d-block">
											{errors.nome}
										</div>
									) : null}
								</div>

								<div className="form-group required d-flex flex-column col-md-4 col-12">
									<label
										htmlFor="email"
										className="form-label"
									>
										Email
									</label>
									<Field
										id="email"
										name="email"
										type="text"
										className="form-control form-field"
									/>
									{errors.email && touched.email ? (
										<div className="invalid-feedback d-block">
											{errors.email}
										</div>
									) : null}
								</div>

								<div className="form-group required d-flex flex-column col-md-4 col-12">
									<label htmlFor="cpf" className="form-label">
										CPF
									</label>
									<Field name="cpf">
										{({ field }) => (
											<InputMask
												{...field}
												mask="999.999.999-99"
												className="form-control form-field"
												placeholder="000.000.000-00"
											/>
										)}
									</Field>
									{errors.cpf && touched.cpf ? (
										<div className="invalid-feedback d-block">
											{errors.cpf}
										</div>
									) : null}
								</div>
							</div>

							<div className="row">
								<div className="form-group required d-flex flex-column col-md-4 col-12">
									<label
										htmlFor="dataNascimento"
										className="form-label"
									>
										Data de Nascimento
									</label>
									<Field
										type="date"
										id="dataNascimento"
										name="dataNascimento"
										className="form-control"
										placeholder="20/08/1970"
										max={minDateFormatted}
									/>
									{errors.dataNascimento &&
									touched.dataNascimento ? (
										<div className="invalid-feedback d-block">
											{errors.dataNascimento}
										</div>
									) : null}
								</div>

								<div className="form-group required d-flex flex-column col-md-4 col-12">
									<label
										htmlFor="telefone"
										className="form-label"
									>
										Telefone
									</label>

									<Field
										type="text"
										id="telefone"
										name="telefone"
										className="form-control"
									>
										{({ field }) => (
											<InputMask
												{...field}
												mask="(99) 99999-9999"
												className="form-control form-field"
											/>
										)}
									</Field>
									{errors.telefone && touched.telefone ? (
										<div className="invalid-feedback d-block">
											{errors.telefone}
										</div>
									) : null}
								</div>

								<div className="form-group required d-flex flex-column col-md-4 col-12">
									<label
										htmlFor="naturalidade"
										className="form-label"
									>
										Naturalidade
									</label>
									<Field
										type="text"
										id="naturalidade"
										name="naturalidade"
										className="form-control"
									/>
									{errors.naturalidade &&
									touched.naturalidade ? (
										<div className="invalid-feedback d-block">
											{errors.naturalidade}
										</div>
									) : null}
								</div>
							</div>

							<div className="row">
								<div className="form-group required col-md-3 col-8">
									<label htmlFor="cep" className="form-label">
										CEP
									</label>
									<div className="d-flex align-items-center gap-2">
										<Field
											name="cep"
											type="text"
											id="cep"
											className="form-control"
										>
											{({ field }) => (
												<InputMask
													{...field}
													mask="99999-999"
													className="form-control form-field"
													onChange={(e) => {
														field.onChange(e);
														fetchAddressByCep(
															e.target.value,
															setFieldValue
														);
													}}
												/>
											)}
										</Field>
										{cepLoading && (
											<ClayLoadingIndicator size="sm" />
										)}
									</div>
									{errors.cep && touched.cep ? (
										<div className="invalid-feedback d-block">
											{errors.cep}
										</div>
									) : null}
								</div>
							</div>

							<div className="row">
								<div className="form-group col-12">
									<label
										htmlFor="endereco"
										className="form-label"
									>
										Endereço
									</label>
									<Field
										type="text"
										id="endereco"
										name="endereco"
										className="form-control"
									/>
								</div>
							</div>

							<div className="row">
								<div className="form-group required col-md-4 col-6">
									<label
										htmlFor="bairro"
										className="form-label"
									>
										Bairro
									</label>
									<Field
										type="text"
										id="bairro"
										name="bairro"
										className="form-control"
									/>
									{errors.bairro && touched.bairro ? (
										<div className="invalid-feedback d-block">
											{errors.bairro}
										</div>
									) : null}
								</div>

								<div className="form-group required col-md-3 col-6">
									<label
										htmlFor="cidade"
										className="form-label"
									>
										Cidade
									</label>
									<Field
										type="text"
										id="cidade"
										name="cidade"
										className="form-control"
									/>
									{errors.cidade && touched.cidade ? (
										<div className="invalid-feedback d-block">
											{errors.cidade}
										</div>
									) : null}
								</div>

								{ufPicklistItems.length > 0 && (
									<div className="form-group required col-md-2 col-4">
										<label
											htmlFor="uf"
											className="form-label"
										>
											Estado
										</label>
										<Field
											as="select"
											id="uf"
											name="uf"
											className="form-control"
											disabled={loading}
										>
											<option value="">UF</option>
											{ufPicklistItems.map((item) => (
												<option
													key={item.key}
													value={item.key}
												>
													{item.name}
												</option>
											))}
										</Field>
										{errors.uf && touched.uf ? (
											<div className="invalid-feedback d-block">
												{errors.uf}
											</div>
										) : null}
									</div>
								)}
							</div>

							<div className="row">
								{sexoPicklistItems.length > 0 && (
									<div className="form-group required col-6 d-flex flex-column">
										<label className="form-label">
											Sexo
										</label>
										<div className="d-flex flex-row radio-group">
											{sexoPicklistItems.map((item) => (
												<label
													key={item.key}
													className="d-flex align-items-center"
												>
													<Field
														type="radio"
														name="sexo"
														value={item.name}
														className="pr-2"
													/>
													{item.name_i18n}
												</label>
											))}
										</div>
									</div>
								)}
							</div>

							<h4 className="mt-3">Dados complementares</h4>

							<div className="row">
								<div className="form-group required d-flex flex-column col-md-6 col-12">
									<label
										htmlFor="nomePai"
										className="form-label"
									>
										Nome do Pai
									</label>
									<Field
										name="nomePai"
										type="text"
										className="form-control form-field"
										placeholder="Leonardo da Silva Santos"
									/>
									{errors.nomePai && touched.nomePai ? (
										<div className="invalid-feedback d-block">
											{errors.nomePai}
										</div>
									) : null}
								</div>

								<div className="form-group required d-flex flex-column col-md-6 col-12">
									<label
										htmlFor="nomeMae"
										className="form-label"
									>
										Nome da Mãe
									</label>
									<Field
										name="nomeMae"
										type="text"
										className="form-control form-field"
										placeholder="Maria Pereira de Almeida"
									/>
									{errors.nomeMae && touched.nomeMae ? (
										<div className="invalid-feedback d-block">
											{errors.nomeMae}
										</div>
									) : null}
								</div>
							</div>

							<div className="row">
								<div className="form-group required col-md-4 col-6">
									<label htmlFor="rg" className="form-label">
										RG
									</label>
									<Field
										id="rg"
										name="rg"
										type="number"
										className="form-control form-field"
									/>
									{errors.rg && touched.rg ? (
										<div className="invalid-feedback d-block">
											{errors.rg}
										</div>
									) : null}
								</div>
								<div className="form-group col-md-4 col-6">
									<label
										htmlFor="orgaoExpeditor"
										className="form-label"
									>
										Órgão Emissor
									</label>
									<Field
										id="orgao-emissor"
										name="orgaoExpeditor"
										type="text"
										className="form-control form-field"
									/>
									{errors.orgaoExpeditor &&
									touched.orgaoExpeditor ? (
										<div className="invalid-feedback d-block">
											{errors.orgaoExpeditor}
										</div>
									) : null}
								</div>
								<div className="form-group col-md-4 col-12">
									<label
										htmlFor="dataEmissao"
										className="form-label"
									>
										Data de Emissão
									</label>
									<Field
										id="dta-emissao"
										name="dataEmissao"
										type="date"
										max={todayDateFormatted}
										className="form-control form-field"
									/>
									{errors.dataEmissao &&
									touched.dataEmissao ? (
										<div className="invalid-feedback d-block">
											{errors.dataEmissao}
										</div>
									) : null}
								</div>
							</div>

							<div className="row">
								{nivelDeEscolaridadePicklistItems.length >
									0 && (
									<div className="form-group col-12 col-md-6">
										<label
											htmlFor="nivelDeEscolaridade"
											className="form-label"
										>
											Escolaridade
										</label>
										<Field
											as="select"
											id="education-level"
											name="nivelDeEscolaridade"
											className="form-control"
											disabled={loading}
										>
											<option value="">Selecione</option>
											{nivelDeEscolaridadePicklistItems.map(
												(item) => (
													<option
														key={item.key}
														value={item.key}
													>
														{item.name}
													</option>
												)
											)}
										</Field>
										{errors.nivelDeEscolaridade &&
										touched.nivelDeEscolaridade ? (
											<div className="invalid-feedback d-block">
												{errors.nivelDeEscolaridade}
											</div>
										) : null}
									</div>
								)}

								{estadoCivilPicklistItems.length > 0 && (
									<div className="form-group col-12 col-md-6">
										<label
											htmlFor="estadoCivil"
											className="form-label"
										>
											Estado Civil
										</label>
										<Field
											as="select"
											id="civil-status"
											name="estadoCivil"
											className="form-control"
											disabled={loading}
										>
											<option value="">Selecione</option>
											{estadoCivilPicklistItems.map(
												(item) => (
													<option
														key={item.key}
														value={item.key}
													>
														{item.name}
													</option>
												)
											)}
										</Field>
										{errors.estadoCivil &&
										touched.estadoCivil ? (
											<div className="invalid-feedback d-block">
												{errors.estadoCivil}
											</div>
										) : null}
									</div>
								)}
							</div>

							<div className="row">
								{tipoSanguineoPicklistItems.length > 0 && (
									<div className="form-group required col-md-2 col-8">
										<label
											htmlFor="tipoSanguineo"
											className="form-label"
										>
											Tipo Sanguíneo
										</label>
										<Field
											as="select"
											id="tipoSanguineo"
											name="tipoSanguineo"
											className="form-control"
											disabled={loading}
										>
											<option value="">Selecione</option>
											{tipoSanguineoPicklistItems.map(
												(item) => (
													<option
														key={item.key}
														value={item.key}
													>
														{item.name}
													</option>
												)
											)}
										</Field>
										{errors.tipoSanguineo &&
										touched.tipoSanguineo ? (
											<div className="invalid-feedback d-block">
												{errors.tipoSanguineo}
											</div>
										) : null}
									</div>
								)}
							</div>
						</div>

						<div className="d-flex justify-content-between">
							<button
								type="button"
								className="btn btn-secondary"
								onClick={onBackStep}
							>
								Anterior
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={isSubmitting}
							>
								Próximo
							</button>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default BaseInfo;
